import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button as MantineButton,
  Image,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title,
  darken,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { SurveyResponseStore, SurveyStore } from '@seedlang/stores';
import { AppUI } from '@seedlang/state';
import Button from 'components/button/button';
import Spinner from 'components/spinner';
import withRouteParams from 'utils/with_route_params';

const optionIsSelected = (answer, optionId, multiple) => {
  if (multiple) {
    return (answer || []).includes(optionId);
  }
  return answer === optionId;
};

const OnboardingSurvey = ({
  accentColor,
  answer = null,
  canContinue,
  completedSteps,
  isDesktop,
  isIntroStep,
  isLastStep,
  onContinue,
  onSelect,
  paymentSuccessHeading,
  paymentSuccessImageUrl,
  secondaryAccentColor,
  siteName,
  step = null,
  stepNumber,
  teamLogoUrl = null,
  totalSteps,
}) => {
  const helperText = isIntroStep
    ? 'Before you start learning, we would like to ask you a few questions to get to know you better.'
    : step.helperText;
  const question = isIntroStep
    ? paymentSuccessHeading
    : step.question;

  return (
    <Stack
      align='center'
      bg='#fff'
      c='#1f1f1f'
      gap={0}
      mih='100dvh'
      pb={isDesktop ? 56 : 40}
      pt={isDesktop ? 25 : 40}
      px={24}
      w='100%'
    >
      <Stack align='center' flex={1} gap={40} maw={562} w='100%'>
        {!isIntroStep && (
          <Stack gap={isDesktop ? 8 : 10} w='100%'>
            <Progress
              aria-label={`Survey progress: step ${stepNumber} of ${totalSteps}`}
              aria-valuemax={totalSteps}
              aria-valuemin='0'
              aria-valuenow={completedSteps}
              color={accentColor}
              radius='xl'
              role='progressbar'
              size={isDesktop ? 8 : 10}
              styles={{root: {backgroundColor: '#f3f3f3'}}}
              value={(completedSteps / totalSteps) * 100}
            />
            <Text
              fz={isDesktop ? 12 : 14}
              lh={isDesktop ? '15px' : '18px'}
              pr={isDesktop ? 6 : 9}
              ta='right'
            >
              {stepNumber}/{totalSteps}
            </Text>
          </Stack>
        )}
        {teamLogoUrl && (
          <Image
            alt={`${siteName} Logo`}
            fit='contain'
            mah={67.5}
            src={teamLogoUrl}
            w='auto'
          />
        )}

        <Stack
          align='center'
          gap={isIntroStep ? 'xs' : isDesktop ? 3 : 7}
        >
          <Title
            ff='inherit'
            fz={isDesktop ? 20 : 24}
            fw={600}
            lh={isDesktop ? '22px' : 1.28}
            order={2}
            style={{textWrap: 'balance'}}
            ta='center'
          >
            {question}
          </Title>
          {helperText && (
            <Text
              c={isIntroStep ? undefined : 'dimmed'}
              fz={isDesktop ? 13 : 16}
              lh={isDesktop ? '18px' : '21px'}
              maw={isIntroStep ? 400 : undefined}
              ta='center'
            >
              {helperText}
            </Text>
          )}
        </Stack>

        {isIntroStep ? (
          <Image
            alt='Membership payment confirmation'
            fit='contain'
            h={250}
            radius='lg'
            src={paymentSuccessImageUrl}
            w='auto'
          />
        ) : (
          <SimpleGrid
            cols={isDesktop || step.compact ? 2 : 1}
            spacing={isDesktop ? 16 : 12}
            verticalSpacing={isDesktop ? 16 : 12}
            w='100%'
          >
            {step.options.map(option => {
              const selected = optionIsSelected(answer, option.id, step.multiple);

              return (
                <MantineButton
                  aria-pressed={selected}
                  color={selected ? secondaryAccentColor : undefined}
                  h={isDesktop ? 72 : 80}
                  key={option.id}
                  leftSection={option.badge ? (
                    <Badge
                      color='#078d2c'
                      fz={isDesktop ? 9 : 12}
                      h={isDesktop ? 15 : 21}
                      miw={isDesktop ? 23 : 33}
                      px={isDesktop ? 5 : 7}
                      radius={isDesktop ? 4 : 6}
                      styles={{
                        root: {
                          backgroundColor: selected ? '#fff' : secondaryAccentColor,
                          color: selected ? secondaryAccentColor : '#fff',
                        },
                      }}
                      tt='none'
                    >
                      {option.badge}
                    </Badge>
                  ) : null}
                  onClick={() => onSelect(option.id)}
                  px={isDesktop ? 15 : 14}
                  py={isDesktop ? 14 : 18}
                  radius={isDesktop ? 6 : 9}
                  styles={{label: {lineHeight: 1.5, whiteSpace: 'normal'}}}
                  type='button'
                  variant={selected ? 'filled' : 'default'}
                >
                  {option.label}
                </MantineButton>
              );
            })}
          </SimpleGrid>
        )}

        <Button
          background={accentColor}
          backgroundOnActive={darken(accentColor, 0.15)}
          backgroundOnHover={darken(accentColor, 0.1)}
          disabled={!canContinue}
          onClick={onContinue}
        >
          {isLastStep ? 'Start Learning' : 'Continue'}
        </Button>
      </Stack>
    </Stack>
  );
};

OnboardingSurvey.propTypes = {
  accentColor: PropTypes.string.isRequired,
  answer: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  canContinue: PropTypes.bool.isRequired,
  completedSteps: PropTypes.number.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isIntroStep: PropTypes.bool.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  onContinue: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  paymentSuccessHeading: PropTypes.string.isRequired,
  paymentSuccessImageUrl: PropTypes.string.isRequired,
  secondaryAccentColor: PropTypes.string.isRequired,
  siteName: PropTypes.string.isRequired,
  step: PropTypes.object,
  stepNumber: PropTypes.number.isRequired,
  teamLogoUrl: PropTypes.string,
  totalSteps: PropTypes.number.isRequired,
};

const LEVEL_BADGES = ['A1', 'A2', 'B1', 'B2', 'C1+'];
const SUPPORTED_QUESTION_TYPES = ['single_choice', 'multiple_choice'];

const surveyIsSupported = survey => (
  survey.metadata?.paymentSuccessHeading &&
  survey.image?.url &&
  survey.surveyQuestions.length > 0 &&
  survey.surveyQuestions.every(question => (
    SUPPORTED_QUESTION_TYPES.includes(question.questionType) &&
    question.surveyQuestionOptions.length > 0
  ))
);

const stepsFromSurvey = (survey, userHasConversationMembership) => survey.surveyQuestions.map((question, questionIndex) => ({
  id: question.id,
  question: question.prompt,
  helperText: question.questionType === 'multiple_choice' ? 'You can select more than one' : null,
  multiple: question.questionType === 'multiple_choice',
  compact: questionIndex >= 2,
  required: question.required,
  options: question.surveyQuestionOptions
    .filter(option => userHasConversationMembership || !/conversation/i.test(option.text))
    .map((option, optionIndex) => ({
      id: option.id,
      badge: questionIndex === 1 ? LEVEL_BADGES[optionIndex] : null,
      label: option.text,
    })),
}));

const OnboardingSurveyWrapper = props => {
  const isDesktop = useMediaQuery('(min-width: 700px)');
  const [answers, setAnswers] = useState({});
  const [stepNumber, setStepNumber] = useState(0);
  const [survey, setSurvey] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const surveySlug = props.params.surveySlug;
  const membershipTypeId = AppUI.user.currentUserGroup?.membershipTypeId;
  const membershipTypeName = AppUI.user.currentUserGroup?.membershipType?.name;

  useEffect(() => {
    let mounted = true;

    const redirectToHome = () => {
      if (!mounted) return;
      setRedirecting(true);
      AppUI.routeToHome();
    };

    if (!membershipTypeId || !membershipTypeName) {
      redirectToHome();
      return () => {
        mounted = false;
      };
    }

    SurveyStore.getBySlug(
      {
        ids: {surveySlug},
        failureCallback: redirectToHome,
        onRequestError: redirectToHome,
      },
      response => {
        if (!mounted) return;
        if (!response?.id || response.completed) {
          redirectToHome();
          return;
        }

        SurveyStore.setShowData(response);
        if (!surveyIsSupported(SurveyStore.showData)) {
          redirectToHome();
          return;
        }
        setSurvey(SurveyStore.showData);
      }
    );

    return () => {
      mounted = false;
      SurveyStore.clearShowData();
    };
  }, [membershipTypeId, membershipTypeName, surveySlug]);

  const submitSurvey = () => {
    SurveyResponseStore.create(
      {
        data: {
          surveyId: survey.id,
          answers: survey.surveyQuestions.map(question => {
            const selected = answers[question.id];
            return {
              surveyQuestionId: question.id,
              surveyQuestionOptionIds: Array.isArray(selected) ? selected : [selected].filter(Boolean),
            };
          }),
        },
      },
      response => {
        if (response.learningPathId && AppUI.user.currentUserGroup) {
          AppUI.user.currentUserGroup.learningPathId = response.learningPathId;
        }
        AppUI.routeToHome();
      }
    );
  };

  if (redirecting) return null;
  if (!survey) {
    return (
      <Stack align='center' justify='center' mih='100dvh'>
        <Spinner />
      </Stack>
    );
  }

  const steps = stepsFromSurvey(
    survey,
    /conversation/i.test(membershipTypeName)
  );
  const isIntroStep = stepNumber === 0;
  const step = isIntroStep ? null : steps[stepNumber - 1];
  const answer = step ? answers[step.id] : null;
  const hasAnswer = step?.multiple ? Boolean(answer?.length) : Boolean(answer);
  const canContinue = isIntroStep || step.required === false || hasAnswer;
  const isLastStep = !isIntroStep && stepNumber === steps.length;
  const completedSteps = isIntroStep ? 0 : stepNumber - 1 + (hasAnswer ? 1 : 0);

  const selectOption = optionId => {
    setAnswers(currentAnswers => {
      const currentAnswer = currentAnswers[step.id];
      let nextAnswer = optionId;

      if (step.multiple) {
        const selectedOptions = currentAnswer || [];
        nextAnswer = selectedOptions.includes(optionId)
          ? selectedOptions.filter(id => id !== optionId)
          : [...selectedOptions, optionId];
      }

      return {...currentAnswers, [step.id]: nextAnswer};
    });
  };

  const continueSurvey = () => {
    if (!canContinue) return;
    if (isLastStep) {
      submitSurvey();
      return;
    }
    setStepNumber(currentStep => currentStep + 1);
  };

  return (
    <OnboardingSurvey
      accentColor={AppUI.site.accentColor}
      answer={answer}
      canContinue={canContinue}
      completedSteps={completedSteps}
      isDesktop={isDesktop}
      isIntroStep={isIntroStep}
      isLastStep={isLastStep}
      onContinue={continueSurvey}
      onSelect={selectOption}
      paymentSuccessHeading={survey.metadata.paymentSuccessHeading}
      paymentSuccessImageUrl={survey.image.url}
      secondaryAccentColor={AppUI.site.secondaryAccentColor}
      siteName={AppUI.site.teamName}
      step={step}
      stepNumber={stepNumber}
      teamLogoUrl={AppUI.site.teamLogo?.image?.url}
      totalSteps={steps.length}
    />
  );
};

export { OnboardingSurvey, OnboardingSurveyWrapper };
export default withRouteParams(OnboardingSurveyWrapper);
